// @ts-strict-ignore
import React from 'react';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import Popover from 'antd/lib/popover';
import Tag from 'antd/lib/tag';
import { antGrey, getAvatarColorForName, runwayColors, tagStyle } from '../../components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Icon, { CheckCircleFilled, EditOutlined, FireFilled, ThunderboltFilled, UserOutlined } from '@ant-design/icons';
import {
    AutomationType,
    AutoReleaseStrategy,
    AutoSubmitStrategiesOnOrAfterTargetDate,
    LifecycleEventType,
    ReleaseCalendarEventType,
    AutomationRun,
    ReleaseStatus,
} from '@/types';
import Avatar from 'antd/lib/avatar';
import {
    displayLifecycleEventFormattedTitle,
    displayUserNameShort,
    displayStringForDestination,
    formatDateString,
    formatEndDateStringForReleaseCalendar,
    getCalendarEventDescription,
    getCalendarEventPopoverIcon,
    displayEventManuallyUpdatedBy,
} from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import { dayjs } from '@/extendedDayjs';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import MarkdownComponent from '../HOCs/Markdown';
import { ReleaseCalendarLifecycleEventData } from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectIsReleaseFetched,
    selectScheduleSettings,
    selectUserById,
} from '@/selectors/selectors';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { tryFetchEntity } from '@/actions/entities';
import { FetchableEntityType } from '@/entityType';
import { Destination } from '@/types/destinations';
import { DestinationIcon } from '@/components/sidebar/shared/components';
import { useMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';

interface ReleaseCyclePopoverProps extends ReleaseComponentProps {
    parentBackgroundColor: string;
    backgroundColor: string;
    eventContent: React.ReactNode;
    displayVersion: string;
    isHotfix: boolean;
    calendarEvents: ReleaseCalendarLifecycleEventData[];
    releasePilotId?: string;
    releaseDescription?: string;
    lifecycleAutomations: AutomationRun[];
    start: string;
    end?: string;
    isSkippedRelease: boolean;
    status: ReleaseStatus;
    isReleaseCompleted: boolean;
    onEditTargetDates?: () => void;
}

const MAX_LABELED_DESTINATIONS = 3;
const MAX_VISIBLE_DESTINATION_ICONS = 8;
const VISIBLE_DESTINATION_ICONS_WITH_COUNTER = 7;
const RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX = 2147483647;
const RELEASE_CYCLE_POPOVER_Z_INDEX = RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX - 1;
const RELEASE_DESCRIPTION_MAX_HEIGHT = 200;

const GROUPABLE_LIFECYCLE_EVENT_TYPES = new Set([
    LifecycleEventType.LifecycleEventTypeKickoff,
    LifecycleEventType.LifecycleEventTypeSubmit,
    LifecycleEventType.LifecycleEventTypeRelease,
]);

interface ReleaseCycleDateGroup {
    key: string;
    representativeEvent: ReleaseCalendarLifecycleEventData;
    events: ReleaseCalendarLifecycleEventData[];
    destinations: Destination[];
}

interface ReleaseCycleEventGroup {
    key: string;
    representativeEvent: ReleaseCalendarLifecycleEventData;
    dateGroups: ReleaseCycleDateGroup[];
}

const destinationIdForDestination = (destination: Destination): string =>
    destination.destinationId ?? `dest-${destination.stepConfigId}`;

const destinationDisplayName = (destination: Destination, platformLabelOverride?: string): string =>
    displayStringForDestination(destination.platform, undefined, undefined, undefined, platformLabelOverride);

const uniqueDestinations = (destinations: Destination[]): Destination[] => {
    const seen = new Set<string>();
    const unique: Destination[] = [];
    destinations.forEach((destination) => {
        const id = destinationIdForDestination(destination);
        if (seen.has(id)) {
            return;
        }
        seen.add(id);
        unique.push(destination);
    });
    return unique;
};

const eventDestinationIds = (event: ReleaseCalendarLifecycleEventData): string[] => {
    if (event.destinationIds?.length) {
        return event.destinationIds;
    }
    return event.destinationId ? [event.destinationId] : [];
};

const formatTooltipDate = (dateString: string): string => {
    const date = dayjs(dateString);
    const timeFormat = date.minute() === 0 ? 'h A' : 'h:mm A';
    return `${formatDateString(dateString, 'ddd, MMM D')} / ${formatDateString(dateString, timeFormat)}`;
};

const ReleaseCyclePopover: React.FC<ReleaseCyclePopoverProps> = (props: ReleaseCyclePopoverProps) => {
    const mdmPlatformLabel = useMdmPlatformOverride(props.appId)?.label;
    const autoSubmitAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoSubmitRelease] ?
            props.lifecycleAutomations[AutomationType.AutoSubmitRelease]
        :   null;
    const autoReleaseAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoRelease] ?
            props.lifecycleAutomations[AutomationType.AutoRelease]
        :   null;

    const scheduleCadenceSettings = useSelector((state: RootState) => selectScheduleSettings(state, props.appId));

    const releasePilotUser = useSelector((state: RootState) => {
        return selectUserById(state, props.orgId, props.appId, props.releasePilotId);
    });
    const dispatch = useDispatch();
    const isReleaseFetched = useSelector((state: RootState) => selectIsReleaseFetched(state, props.releaseId));

    // The app-level calendar doesn't load release entities into the store, so fetch the
    // release lazily on popover open — the edit-target-dates modal needs it to seed its form.
    const canEditTargetDates =
        !!props.onEditTargetDates &&
        props.status !== ReleaseStatus.Predicted &&
        !props.isSkippedRelease &&
        !props.isReleaseCompleted;
    // Controlled so the edit-target-dates button can dismiss the popover — its z-index sits
    // above the modal's, so leaving it open would cover the modal.
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const hasRequestedReleaseFetchRef = React.useRef(false);
    const handlePopoverOpenChange = (open: boolean) => {
        setPopoverOpen(open);
        if (!open || !canEditTargetDates || isReleaseFetched || hasRequestedReleaseFetchRef.current) {
            return;
        }
        hasRequestedReleaseFetchRef.current = true;
        dispatch(
            tryFetchEntity(
                FetchableEntityType.Release,
                { orgId: props.orgId, appId: props.appId, releaseId: props.releaseId },
                props.releaseId,
            ),
        );
    };

    const handleEditTargetDatesClick = () => {
        setPopoverOpen(false);
        props.onEditTargetDates?.();
    };
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, props.appId));
    const destinationInfoById = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, props.appId),
    );

    const filteredEvents = props.calendarEvents.filter(
        (event) =>
            event.eventType != LifecycleEventType.LifecycleEventTypeRolloutIncrement &&
            event.eventType != LifecycleEventType.LifecycleEventTypeBlackoutPeriod,
    );

    const destinationById = React.useMemo(() => {
        const byId: Record<string, Destination> = {};
        Object.values(destinationInfoById).forEach((destination) => {
            byId[destination.stepConfigId] = destination;
            byId[destinationIdForDestination(destination)] = destination;
            if (destination.destinationId) {
                byId[destination.destinationId] = destination;
            }
        });
        return byId;
    }, [destinationInfoById]);

    const destinationsForEvent = React.useCallback(
        (event: ReleaseCalendarLifecycleEventData): Destination[] =>
            uniqueDestinations(
                eventDestinationIds(event)
                    .map((destinationId) => destinationById[destinationId])
                    .filter(Boolean),
            ),
        [destinationById],
    );

    const releaseDestinations = React.useMemo(() => {
        const destinations: Destination[] = [];
        props.calendarEvents.forEach((event) => {
            destinations.push(...destinationsForEvent(event));
        });
        return uniqueDestinations(destinations);
    }, [destinationsForEvent, props.calendarEvents]);

    const groupedEvents = React.useMemo(() => {
        const groups: ReleaseCycleEventGroup[] = [];
        const groupByKey: Record<string, ReleaseCycleEventGroup> = {};

        filteredEvents.forEach((event) => {
            const canGroupEventType = GROUPABLE_LIFECYCLE_EVENT_TYPES.has(event.eventType);
            const groupKey =
                canGroupEventType ?
                    `${event.eventType}|${event.eventDateType}|${event.isEventComplete}|${event.isEventAutomated}`
                :   event.id;
            let eventGroup = groupByKey[groupKey];
            if (!eventGroup) {
                eventGroup = {
                    key: groupKey,
                    representativeEvent: event,
                    dateGroups: [],
                };
                groupByKey[groupKey] = eventGroup;
                groups.push(eventGroup);
            }

            const dateGroupKey = canGroupEventType ? `${dayjs(event.start).valueOf()}` : event.id;
            let dateGroup = eventGroup.dateGroups.find((group) => group.key === dateGroupKey);
            if (!dateGroup) {
                dateGroup = {
                    key: dateGroupKey,
                    representativeEvent: event,
                    events: [],
                    destinations: [],
                };
                eventGroup.dateGroups.push(dateGroup);
            }

            dateGroup.events.push(event);
            dateGroup.destinations = uniqueDestinations([...dateGroup.destinations, ...destinationsForEvent(event)]);
        });

        groups.forEach((group) => {
            group.dateGroups.sort(
                (a, b) => dayjs(a.representativeEvent.start).valueOf() - dayjs(b.representativeEvent.start).valueOf(),
            );
        });

        return groups.sort(
            (a, b) => dayjs(a.representativeEvent.start).valueOf() - dayjs(b.representativeEvent.start).valueOf(),
        );
    }, [destinationsForEvent, filteredEvents]);

    const renderEditTargetDatesButton = () => {
        if (!props.onEditTargetDates) {
            return null;
        }

        let disabledReason: string | null = null;
        if (props.status === ReleaseStatus.Predicted) {
            disabledReason = 'Release is predicted, cannot update target dates';
        } else if (props.isSkippedRelease) {
            disabledReason = 'Release is skipped, cannot update target dates';
        } else if (props.isReleaseCompleted) {
            disabledReason = 'Release is completed, cannot update target dates';
        } else if (!isReleaseFetched) {
            disabledReason = 'Release data is still loading';
        }

        return (
            <Tooltip title={disabledReason ?? 'Edit target dates'} zIndex={RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX}>
                {/* span wrapper so the tooltip still fires when the button is disabled */}
                <span style={{ marginLeft: 8 }}>
                    <PermissionedActionButton
                        icon={
                            <EditOutlined
                                style={{ color: runwayColors.white, opacity: disabledReason ? 0.5 : 1, fontSize: 16 }}
                            />
                        }
                        type="text"
                        size="small"
                        disabled={!!disabledReason}
                        onClick={handleEditTargetDatesClick}
                    />
                </span>
            </Tooltip>
        );
    };

    const title = (
        <div
            style={{
                margin: 0,
                padding: 8,
                backgroundColor: props.parentBackgroundColor,
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <Tag color={runwayColors.white} style={{ ...tagStyle, border: `1px solid ${runwayColors.white}` }}>
                    <Text style={{ color: props.parentBackgroundColor }}>{props.displayVersion}</Text>
                </Tag>
                {props.isHotfix && (
                    <Tag
                        color={props.parentBackgroundColor}
                        style={{ ...tagStyle, border: `1px solid ${runwayColors.white}` }}
                    >
                        <FireFilled />
                        <Text
                            style={{
                                color: runwayColors.white,
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: 10,
                                lineHeight: '23px',
                            }}
                        >
                            Hotfix
                        </Text>
                    </Tag>
                )}
                {props.isSkippedRelease && (
                    <Tag
                        color={props.parentBackgroundColor}
                        style={{ ...tagStyle, border: `1px dashed ${runwayColors.white}` }}
                    >
                        <Text
                            style={{
                                color: runwayColors.white,
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: 10,
                                lineHeight: '23px',
                            }}
                        >
                            Skipped
                        </Text>
                    </Tag>
                )}
                {props.start && props.end && (
                    <Text
                        style={{
                            color: runwayColors.white,
                            fontSize: 16,
                            fontWeight: 'normal',
                            textDecoration: props.isSkippedRelease ? 'line-through' : '',
                        }}
                    >
                        {formatDateString(props.start, 'MMM D')} -{' '}
                        {formatEndDateStringForReleaseCalendar(props.end, 'MMM D')}
                    </Text>
                )}
                {renderEditTargetDatesButton()}
            </span>
        </div>
    );

    const renderDestinationTooltipContent = (destinations: Destination[]) => (
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {destinations.map((destination) => (
                <div
                    key={destinationIdForDestination(destination)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                >
                    <DestinationIcon destination={destination} size={16} />
                    <Text style={{ color: runwayColors.white }}>
                        {destinationDisplayName(destination, mdmPlatformLabel)}
                    </Text>
                </div>
            ))}
        </div>
    );

    const renderDestinationCounter = (destinations: Destination[]) => {
        if (destinations.length <= 1) {
            return null;
        }

        return (
            <Tooltip
                title={renderDestinationTooltipContent(destinations)}
                zIndex={RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 16,
                        height: 16,
                        padding: '0 5px',
                        borderRadius: 8,
                        backgroundColor: props.parentBackgroundColor,
                        color: runwayColors.white,
                        fontSize: 10,
                        fontWeight: 600,
                        lineHeight: '16px',
                    }}
                >
                    {destinations.length}
                </span>
            </Tooltip>
        );
    };

    const renderDestinations = () => {
        if (releaseDestinations.length === 0) {
            return null;
        }

        const useCompactDisplay = releaseDestinations.length > MAX_LABELED_DESTINATIONS;
        const visibleDestinationCount =
            releaseDestinations.length > MAX_VISIBLE_DESTINATION_ICONS ?
                VISIBLE_DESTINATION_ICONS_WITH_COUNTER
            :   releaseDestinations.length;
        const visibleDestinations =
            useCompactDisplay ? releaseDestinations.slice(0, visibleDestinationCount) : releaseDestinations;
        const hiddenDestinations = useCompactDisplay ? releaseDestinations.slice(visibleDestinationCount) : [];

        return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {visibleDestinations.map((destination) => (
                    <Tooltip
                        key={destinationIdForDestination(destination)}
                        title={destinationDisplayName(destination, mdmPlatformLabel)}
                        zIndex={RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX}
                    >
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                minWidth: useCompactDisplay ? 40 : 74,
                                maxWidth: useCompactDisplay ? 40 : 132,
                                height: useCompactDisplay ? 40 : 38,
                                padding: useCompactDisplay ? 0 : '0 10px',
                                borderRadius: 4,
                                backgroundColor: antGrey.grey4,
                                border: `1px solid ${antGrey.grey5}`,
                                boxSizing: 'border-box',
                            }}
                        >
                            <DestinationIcon destination={destination} size={useCompactDisplay ? 28 : 18} />
                            {!useCompactDisplay && (
                                <Text
                                    style={{
                                        display: 'block',
                                        minWidth: 0,
                                        color: antGrey.grey7,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {destinationDisplayName(destination, mdmPlatformLabel)}
                                </Text>
                            )}
                        </div>
                    </Tooltip>
                ))}
                {hiddenDestinations.length > 0 && (
                    <Tooltip
                        title={renderDestinationTooltipContent(hiddenDestinations)}
                        zIndex={RELEASE_CYCLE_NESTED_TOOLTIP_Z_INDEX}
                    >
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: 4,
                                backgroundColor: antGrey.grey2,
                                border: `1px solid ${antGrey.grey4}`,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Text style={{ color: antGrey.grey7 }}>+{hiddenDestinations.length}</Text>
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    };

    const renderEventDate = (dateGroup: ReleaseCycleDateGroup) => {
        const event = dateGroup.representativeEvent;
        const submitEnabledNoEarlierThanTarget =
            event.eventType === LifecycleEventType.LifecycleEventTypeSubmit &&
            autoSubmitAutomation &&
            autoSubmitAutomation.enabled &&
            AutoSubmitStrategiesOnOrAfterTargetDate.includes(autoSubmitAutomation.data.submitStrategy);
        const releaseEnabledNoEarlierThanTarget =
            event.eventType === LifecycleEventType.LifecycleEventTypeRelease &&
            autoReleaseAutomation &&
            autoReleaseAutomation.enabled &&
            autoReleaseAutomation.data.strategy === AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate;
        const noEarlierThanTargetEnabled = submitEnabledNoEarlierThanTarget || releaseEnabledNoEarlierThanTarget;
        const icon = getCalendarEventPopoverIcon(
            event.eventType,
            event.eventDateType,
            event.start,
            noEarlierThanTargetEnabled,
            event.targetDateSetByCadence,
        );
        const description = getCalendarEventDescription(
            event.eventType,
            event.eventDateType,
            noEarlierThanTargetEnabled,
            scheduleCadenceSettings,
            event.targetDateSetByCadence,
            event.status === ReleaseStatus.Skipped,
        );

        const showManualUpdate =
            event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
            !!event.targetDateManuallyUpdatedAt;
        const manualUpdatedByUser =
            event.targetDateManuallyUpdatedByUserId ?
                appUsers.find((user) => user.id === event.targetDateManuallyUpdatedByUserId)
            :   null;
        const manualUpdateText = displayEventManuallyUpdatedBy(event.targetDateManuallyUpdatedAt, manualUpdatedByUser);
        const textStyle = {
            fontSize: 14,
            color:
                event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ?
                    antGrey.grey7
                :   antGrey.grey8,
            fontWeight: 400,
        };

        let date = <Text style={textStyle}>{formatTooltipDate(event.start)}</Text>;
        if (
            props.isSkippedRelease &&
            (event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
                event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
        ) {
            date = (
                <Text delete style={textStyle}>
                    {formatTooltipDate(event.start)}
                </Text>
            );
        }

        return (
            <div key={dateGroup.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Space size={8}>
                    {renderDestinationCounter(dateGroup.destinations)}
                    {showManualUpdate && (
                        <Tooltip title={manualUpdateText} zIndex={2147483647}>
                            <UserOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                        </Tooltip>
                    )}
                    {icon && <Icon component={icon} style={{ color: antGrey.grey7, fontSize: 14 }} />}
                    {date}
                </Space>
                {description && (
                    <Text style={{ fontSize: 12, color: antGrey.grey6, textAlign: 'right' }}>{description}</Text>
                )}
            </div>
        );
    };

    const renderEventDates = () => {
        if (groupedEvents.length === 0) {
            return null;
        }

        return (
            <div
                style={{
                    backgroundColor: antGrey.grey2,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${antGrey.grey5}`,
                    width: 402,
                    boxSizing: 'border-box',
                }}
            >
                {groupedEvents.map((eventGroup, index) => {
                    const event = eventGroup.representativeEvent;
                    const title = displayLifecycleEventFormattedTitle(
                        event.eventType,
                        event.eventDateType,
                        event.isEventComplete,
                    );
                    const eventGroupEvents = eventGroup.dateGroups.reduce(
                        (events, dateGroup) => events.concat(dateGroup.events),
                        [] as ReleaseCalendarLifecycleEventData[],
                    );

                    return (
                        <div
                            key={eventGroup.key}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 16,
                                paddingTop: index === 0 ? 0 : 10,
                                marginTop: index === 0 ? 0 : 10,
                                borderTop: index === 0 ? undefined : `1px solid ${antGrey.grey4}`,
                            }}
                        >
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 140 }}>
                                <Text
                                    style={{
                                        fontSize: 12,
                                        color: antGrey.grey7,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        fontWeight: 400,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {title}
                                </Text>
                                <ReleaseCycleLifecycleEventIcon events={eventGroupEvents} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {eventGroup.dateGroups.map(renderEventDate)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const content = (
        <div style={{ padding: 10, paddingBottom: 20, margin: 10, opacity: props.isSkippedRelease ? 0.55 : 1 }}>
            {renderDestinations()}
            {renderEventDates()}
            {releasePilotUser && (
                <>
                    <div style={{ marginTop: 15, marginBottom: 6 }}>
                        <Text
                            style={{
                                fontSize: '12px',
                                color: antGrey.grey7,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                marginRight: 8,
                                fontWeight: 400,
                            }}
                        >
                            Release Pilot
                        </Text>
                    </div>
                    <div style={{ marginBottom: 15 }}>
                        <Avatar
                            key={releasePilotUser.id}
                            src={releasePilotUser.commsAvatars?.image32}
                            style={{
                                backgroundColor: getAvatarColorForName(displayUserNameShort(releasePilotUser)),
                                marginRight: 10,
                                fontSize: 12,
                            }}
                            size={25}
                        >
                            {displayUserNameShort(releasePilotUser).charAt(0).toUpperCase()}
                        </Avatar>
                        <Text>{displayUserNameShort(releasePilotUser)}</Text>
                    </div>
                </>
            )}
            {props.releaseDescription && (
                <div>
                    <Divider style={{ marginTop: 10, marginBottom: 10, borderColor: antGrey.grey5 }} />
                    <div style={{ marginBottom: 6 }}>
                        <Text
                            style={{
                                fontSize: '12px',
                                color: antGrey.grey7,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                marginRight: 8,
                                fontWeight: 400,
                            }}
                        >
                            Release Description
                        </Text>
                    </div>
                    <ReleaseDescriptionPreview source={props.releaseDescription} />
                </div>
            )}
        </div>
    );

    return (
        <Popover
            content={content}
            title={title}
            open={popoverOpen}
            onOpenChange={handlePopoverOpenChange}
            arrow={false}
            placement="top"
            align={{ overflow: { adjustX: 1, adjustY: 0 } }}
            zIndex={RELEASE_CYCLE_POPOVER_Z_INDEX}
            overlayStyle={{
                padding: 0,
                margin: 0,
                zIndex: RELEASE_CYCLE_POPOVER_Z_INDEX,
            }}
            overlayClassName="release-cycle-popover"
        >
            {props.eventContent}
        </Popover>
    );
};

const ReleaseDescriptionPreview: React.FC<{ source: string }> = ({ source }) => (
    <div
        style={{
            maxWidth: 300,
            maxHeight: RELEASE_DESCRIPTION_MAX_HEIGHT,
            overflowY: 'auto',
            // stop wheel events from scrolling the calendar underneath once the description hits its end
            overscrollBehavior: 'contain',
            paddingRight: 4,
            overflowWrap: 'break-word',
        }}
    >
        <MarkdownComponent source={source} />
    </div>
);

const ReleaseCycleLifecycleEventIcon: React.FC<{
    events: ReleaseCalendarLifecycleEventData[];
}> = ({ events }) => {
    if (
        events.some(
            (event) =>
                event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate &&
                event.isEventComplete,
        )
    ) {
        return <CheckCircleFilled style={{ color: runwayColors.statusGreen, fontSize: 12 }} />;
    }

    if (events.some((event) => event.isEventAutomated)) {
        return <ThunderboltFilled style={{ color: antGrey.grey6, fontSize: 12 }} />;
    }

    return null;
};

export default ReleaseCyclePopover;
